/* Intelligems Test hyp-vg-027 / Versa-T6-PDP-IAO: Pro PDP template redesign. Variant: pro-iao-test-template. Added: 2026-07-23. Remove/merge: when test concludes. */

/*
  vg-pro-gallery.js — defines <vg-media-gallery>, a native CSS scroll-snap
  gallery (no legacy slider engine, no transform/drag). Progressive
  enhancement only: the track scrolls and snaps natively with this script
  absent; JS adds thumb-rail active-state sync, thumb-click scrolling, and
  the updateGallery(mediaId) hook variant selection calls.

  Call contract (porting map §0/§3, global.js ~2551-2559):
    document.getElementById(`Media-Gallery-${sectionId}`).updateGallery(mediaId)
  is invoked by HighVariantSelects.updateMedia() whenever the selected
  variant's featured_media changes. Signature and no-op-on-same-id/null
  behavior mirror the theme's own media-gallery.js.
*/

if (!customElements.get("vg-media-gallery")) {
  customElements.define(
    "vg-media-gallery",
    class VgMediaGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector(".vg-gallery-track");
        this.slides = Array.from(this.querySelectorAll(".vg-gallery-slide"));
        this.thumbs = Array.from(this.querySelectorAll(".vg-gallery-thumb"));
        this.counterCurrent = this.querySelector(".vg-gallery-counter-current");

        if (!this.track || this.slides.length === 0) return;

        this.lastMediaId = this.slides[0].dataset.mediaId || null;

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener("click", this.onThumbClick.bind(this));
        });

        // Rail arrows page the thumb scroller by ~3 thumbs. The rail is a vertical
        // column on desktop and a horizontal strip on mobile, so pick the axis from
        // the element rather than the breakpoint — same test scrollThumbIntoView uses.
        this.querySelectorAll(".vg-gallery-thumbs-arrow").forEach((arrow) => {
          arrow.addEventListener("click", () => {
            const rail = this.querySelector(".vg-gallery-thumbs");
            if (!rail) return;
            const firstThumb = this.thumbs[0];
            const thumbSize = firstThumb
              ? firstThumb.getBoundingClientRect().width + 8
              : 96;
            const step = 3 * thumbSize;
            const dir = arrow.classList.contains("vg-gallery-thumbs-arrow--up") ? -1 : 1;
            const vertical = rail.scrollHeight > rail.clientHeight + 1;
            rail.scrollBy(
              vertical
                ? { top: dir * step, behavior: "smooth" }
                : { left: dir * step, behavior: "smooth" },
            );
          });
        });

        this.observeSlides();
      }

      /**
       * Called by HighVariantSelects.updateMedia() (global.js) whenever the
       * selected variant's featured_media changes. Scrolls the track to the
       * matching slide and syncs the thumb rail + counter.
       */
      updateGallery(mediaId) {
        if (mediaId == null) return;
        const mediaIdString = String(mediaId);
        if (mediaIdString === String(this.lastMediaId)) return;
        this.lastMediaId = mediaIdString;

        const targetSlide = this.slides.find(
          (slide) => slide.dataset.mediaId === mediaIdString,
        );
        if (!targetSlide) return;

        this.slides.forEach((slide) =>
          slide.classList.remove("is-current-variant-media"),
        );
        targetSlide.classList.add("is-current-variant-media");

        // hide_variants parity: the current variant's thumb must stay visible
        // even when it isn't the click-active one (CSS keys on this class).
        this.thumbs.forEach((thumb) =>
          thumb.classList.toggle(
            "vg-thumb--current-variant",
            thumb.dataset.target === mediaIdString,
          ),
        );

        this.scrollTrackTo(targetSlide);
        this.setActiveThumb(mediaIdString);
      }

      onThumbClick(event) {
        const targetId = event.currentTarget.dataset.target;
        const targetSlide = this.slides.find(
          (slide) => slide.dataset.mediaId === targetId,
        );
        if (!targetSlide) return;

        this.scrollTrackTo(targetSlide);
        this.setActiveThumb(targetId);
      }

      /**
       * Scrolls the track horizontally only — computed via getBoundingClientRect
       * deltas rather than scrollIntoView so a slide change never triggers a
       * vertical page scroll (vertical pan over the gallery must stay native
       * page scroll per spec §6).
       */
      scrollTrackTo(targetSlide) {
        const trackRect = this.track.getBoundingClientRect();
        const targetRect = targetSlide.getBoundingClientRect();
        const delta = targetRect.left - trackRect.left;
        this.track.scrollTo({
          left: this.track.scrollLeft + delta,
          behavior: "smooth",
        });
      }

      setActiveThumb(mediaId) {
        this.thumbs.forEach((thumb) => {
          const isActive = thumb.dataset.target === mediaId;
          thumb.classList.toggle("is-active", isActive);
          if (isActive) {
            thumb.setAttribute("aria-current", "true");
            this.scrollThumbIntoView(thumb);
          } else {
            thumb.removeAttribute("aria-current");
          }
        });
      }

      scrollThumbIntoView(thumb) {
        const rail = thumb.closest(".vg-gallery-thumbs");
        if (!rail) return;
        const railRect = rail.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
        // vertical rail (desktop v3) vs horizontal strip (mobile)
        if (rail.scrollHeight > rail.clientHeight + 1) {
          if (thumbRect.top >= railRect.top && thumbRect.bottom <= railRect.bottom) return;
          const dy = thumbRect.top - railRect.top - railRect.height / 2 + thumbRect.height / 2;
          rail.scrollTo({ top: rail.scrollTop + dy, behavior: "smooth" });
          return;
        }
        if (thumbRect.left >= railRect.left && thumbRect.right <= railRect.right) {
          return;
        }
        const delta = thumbRect.left - railRect.left - railRect.width / 2 + thumbRect.width / 2;
        rail.scrollTo({ left: rail.scrollLeft + delta, behavior: "smooth" });
      }

      /**
       * Tracks which slide is currently in view (mobile swipe or desktop
       * scroll, not just JS-driven scrolls) to keep the thumb rail + counter
       * pill in sync with the native scroll-snap track.
       */
      observeSlides() {
        if (!("IntersectionObserver" in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            const mostVisible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (!mostVisible) return;

            const mediaId = mostVisible.target.dataset.mediaId;
            this.lastMediaId = mediaId;
            this.setActiveThumb(mediaId);

            if (this.counterCurrent) {
              // index among VISIBLE slides only (hide_variants hides some)
              const visibleSlides = this.slides.filter(
                (slide) => slide.offsetParent !== null,
              );
              const index = visibleSlides.indexOf(mostVisible.target);
              if (index > -1) this.counterCurrent.textContent = index + 1;
            }
          },
          { root: this.track, threshold: 0.6 },
        );

        this.slides.forEach((slide) => this.observer.observe(slide));
      }
    },
  );
}
